import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { verifyEmail } from '../api';
import { useAuth } from '../context/AuthContext';

type State = 'verifying' | 'success' | 'invalid' | 'expired' | 'used' | 'missing';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<State>('verifying');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) { setState('missing'); return; }
    verifyEmail(token)
      .then(async () => {
        setState('success');
        // Hvis brugeren er logget ind, refresh state så de kan komme videre
        if (user) await refresh();
      })
      .catch((err: Error) => {
        if (err.message === 'token_expired') setState('expired');
        else if (err.message === 'token_already_used') setState('used');
        else setState('invalid');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        {state === 'verifying' && (
          <>
            <div className="mx-auto w-12 h-12 border-4 border-[var(--color-border)] border-t-[var(--color-primary)] rounded-full animate-spin mb-4" />
            <p className="text-sm text-gray-600">Verificerer din email…</p>
          </>
        )}

        {state === 'success' && (
          <>
            <div className="mx-auto w-12 h-12 bg-[var(--color-success-100)] rounded-full flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-[var(--color-success)]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Email bekræftet</h1>
            <p className="text-sm text-gray-600 mb-4">
              {user?.customerStatus === 'pending_review'
                ? 'Din konto er nu verificeret men afventer manuel godkendelse. Du får besked når den er klar.'
                : 'Du kan nu bruge RentalFleetOS.'}
            </p>
            <button onClick={() => navigate('/')} className="text-sm text-[var(--color-primary)] hover:underline">
              Gå til app
            </button>
          </>
        )}

        {(state === 'expired' || state === 'used' || state === 'invalid' || state === 'missing') && (
          <>
            <div className="mx-auto w-12 h-12 bg-[var(--color-error-100)] rounded-full flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-[var(--color-error)]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {state === 'expired' && 'Link er udløbet'}
              {state === 'used' && 'Link er allerede brugt'}
              {state === 'invalid' && 'Ugyldigt link'}
              {state === 'missing' && 'Manglende token'}
            </h1>
            <p className="text-sm text-gray-600 mb-4">
              {state === 'expired' && 'Verifikations-linket er udløbet. Log ind og bed om et nyt.'}
              {state === 'used' && 'Dette link er allerede blevet brugt. Prøv at logge ind.'}
              {state === 'invalid' && 'Linket er ugyldigt. Tjek at du har kopieret hele URL\'en korrekt.'}
              {state === 'missing' && 'Linket mangler en token-parameter.'}
            </p>
            <Link to="/" className="text-sm text-[var(--color-primary)] hover:underline">Gå til log ind</Link>
          </>
        )}
      </div>
    </div>
  );
}
